import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@webapp/common/components/button';
import { Alert } from '@webapp/common/components/message';
import { Modal } from '@webapp/common/components/modal';
import {
    DEFAULT_PHONE,
    getPhoneDigits,
    isPhoneComplete,
    PhoneInput,
} from '@webapp/common/components/phone-input/phone-input';
import type { PhoneValue } from '@webapp/common/components/phone-input/phone-input';
import { Textfield, TextfieldType } from '@webapp/common/components/textfield';
import { ApiRespCode } from '@webapp/common/lib/api';
import { RecipientPhoneAuthType } from '@webapp/common/resources/survey';

import { surveyApi } from '@webapp/survey/src/machine/api';

import css from './confirm.css';

interface Props {
    onConfirm: () => void;
    authHash?: string;
    preservePhone?: string;
    recipientPhoneAuthType?: RecipientPhoneAuthType;
}

export const Confirm: FC<Props> = ({ onConfirm, authHash, preservePhone, recipientPhoneAuthType }) => {
    const disabledPhone = Boolean(preservePhone);
    const smsOnly = recipientPhoneAuthType === RecipientPhoneAuthType.SMS;
    const [phone, setPhone] = useState<PhoneValue>(DEFAULT_PHONE);
    const [showConfirm, setShowConfirm] = useState<boolean>(disabledPhone);
    const [confirmValue, setConfirmValue] = useState<string>('');
    const [disabledResend, setDisabledResend] = useState<boolean>(true);
    const [timer, setTimer] = useState<number>(0);
    const timerRef = useRef(null);

    const countResendBtnTimer = (newTimer: number) => {
        setTimer(newTimer);

        if (newTimer > 0) {
            timerRef.current = setTimeout(() => {
                countResendBtnTimer(newTimer - 1000);
            }, 1000);
        } else {
            setDisabledResend(false);
        }
    };

    const sendPhone = () => {
        surveyApi
            .sendPhoneForConfirmation(authHash ? { authHash } : { phone: getPhoneDigits(phone) })
            // @ts-ignore
            .then((resp: { code: number; message: string }) => {
                if (resp.code === ApiRespCode.OK_CODE) {
                    Alert.success('Запрос на подтверждение отправлен');

                    setShowConfirm(true);
                    setDisabledResend(true);
                    countResendBtnTimer(60000);
                } else {
                    Alert.error(resp.message);
                }
            })
            .catch((e) => {
                Alert.error(e?.message || e);
            });
    };

    const confirmRequest = () => {
        surveyApi
            .confirmAuthCode({
                authHash,
                code: String(confirmValue),
                phone: authHash ? undefined : getPhoneDigits(phone),
            })
            // @ts-ignore
            .then((resp: { code: number; message: string; token: string }) => {
                if (resp.code === ApiRespCode.OK_CODE) {
                    surveyApi.setToken(resp.token);
                    onConfirm();
                } else {
                    Alert.error(resp.message);
                }
            })
            .catch((e) => {
                Alert.error(e?.message || e);
            });
    };

    useEffect(() => {
        if (authHash) {
            sendPhone();
        }
    }, [authHash]);

    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
        };
    }, []);

    return (
        <Modal closeBtn={false} visible={true} className={css.modal} disableOutsideClick={true}>
            {!showConfirm ? (
                <>
                    Введите номер телефона, чтобы подтвердить отправку ответа.{' '}
                    {smsOnly ? 'Вам поступит смс сообщение с кодом' : 'Вам поступит звонок или смс сообщение с кодом'}
                    <div className={css.actions}>
                        <PhoneInput value={phone} disabled={disabledPhone} onChange={setPhone} />
                        <Button
                            color='blue'
                            className={css.button}
                            disabled={!isPhoneComplete(phone)}
                            onClick={sendPhone}
                        >
                            Отправить
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div>
                        На ваш номер телефона <b>*******{(preservePhone || getPhoneDigits(phone)).slice(-4)}</b>{' '}
                        {smsOnly
                            ? 'поступит смс сообщение с кодом. Необходимо ввести код из смс сообщения.'
                            : 'поступит звонок. Необходимо ввести последние 4 цифры телефонного номера, с которого поступит звонок. Если звонок не пройдёт, то будет отправлено смс сообщение с кодом.'}
                    </div>
                    <div className={css.actions}>
                        <Textfield
                            type={TextfieldType.IMASK_NUMBER}
                            defaultValue={confirmValue}
                            onChange={setConfirmValue}
                        />
                        <Button color='blue' className={css.button} onClick={confirmRequest}>
                            Подтвердить
                        </Button>
                    </div>
                    <div>
                        <div className={css.links}>
                            <span
                                className={cn(css.link, { [css.disabled]: disabledResend })}
                                onClick={disabledResend ? undefined : sendPhone}
                            >
                                Отправить код повторно
                            </span>
                            {!disabledPhone && (
                                <span
                                    className={css.link}
                                    onClick={() => {
                                        clearTimeout(timerRef.current);
                                        setShowConfirm(false);
                                    }}
                                >
                                    Изменить телефон
                                </span>
                            )}
                        </div>
                        <div className={cn(css.timer, { [css.timer_hidden]: !disabledResend })}>
                            Повторный запрос возможен через: <b>{timer / 1000}</b> сек
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};
